import moment from "moment";

import { httpGetPrivateInvestimentFundDatas, httpGetRateHistory } from "services/private_investment_funds";
import { httpGetPrivateStockDatas } from "services/private_stocks";
import { httpGetInvestmentFundData } from "services/investment_fund";
import { httpGetStockData } from "services/stocks";

const DEFAULT_THRESHOLD = 1.5;
const DEFAULT_WINDOW_DAYS = 7;

const PRICE_KEYS = [
  "updated_price",
  "price",
  "ending_price",
  "updated_ending_price",
  "closing_price",
];

const DATE_KEYS = ["date", "stock_date"];

const parsePrice = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const getPointPrice = (item) => {
  if (!item || typeof item !== "object") return null;
  for (let i = 0; i < PRICE_KEYS.length; i += 1) {
    const price = parsePrice(item[PRICE_KEYS[i]]);
    if (price != null && price > 0) return price;
  }
  return null;
};

const getPointTime = (item) => {
  if (!item || typeof item !== "object") return null;
  for (let i = 0; i < DATE_KEYS.length; i += 1) {
    const raw = item[DATE_KEYS[i]];
    if (!raw) continue;
    const parsed = moment(raw);
    if (parsed.isValid()) return parsed.valueOf();
  }
  return null;
};

export const toRateArray = (latestData) => {
  if (!latestData) return [];
  if (Array.isArray(latestData)) return latestData;
  if (typeof latestData === "string") {
    try {
      return toRateArray(JSON.parse(latestData));
    } catch (e) {
      return [];
    }
  }
  if (Array.isArray(latestData.results)) return latestData.results;
  if (Array.isArray(latestData.data)) return latestData.data;
  if (latestData.date || latestData.stock_date) return [latestData];
  return [];
};

export const pickMomentumSeries = (row, fetched) => {
  const list = toRateArray(row?.latest_data);
  const extra = toRateArray(fetched);
  return extra.length > list.length ? extra : list;
};

const toPoints = (latestData) =>
  toRateArray(latestData)
    .map((item) => ({ price: getPointPrice(item), time: getPointTime(item) }))
    .filter((p) => p.price != null && p.time != null)
    .sort((a, b) => a.time - b.time);

/**
 * 7 pv hintasuunta kurssipisteistä.
 * Ikkuna lasketaan viimeisestä pisteestä (ei Date.now), jotta harvat kirjaukset toimivat.
 * @returns {{ pct: number|null, tone: "up"|"flat"|"down"|null }}
 */
export const getSparklineMomentum = (
  latestData,
  { threshold = DEFAULT_THRESHOLD, windowDays = DEFAULT_WINDOW_DAYS } = {}
) => {
  const points = toPoints(latestData);
  if (points.length < 2) {
    return { pct: null, tone: null };
  }

  const lastTime = points[points.length - 1].time;
  const cutoff = lastTime - windowDays * 24 * 60 * 60 * 1000;
  const inWindow = points.filter((p) => p.time >= cutoff);
  const series = inWindow.length >= 2 ? inWindow : points;

  const first = series[0].price;
  const last = series[series.length - 1].price;
  if (!first) {
    return { pct: null, tone: null };
  }

  const pct = ((last - first) / first) * 100;
  if (!Number.isFinite(pct)) {
    return { pct: null, tone: null };
  }

  let tone = "flat";
  if (pct > threshold) tone = "up";
  else if (pct < -threshold) tone = "down";

  return { pct, tone };
};

export const formatMomentumPct = (pct, language = "fi") => {
  if (pct == null || !Number.isFinite(pct)) return "";
  const abs = Math.abs(pct).toFixed(1);
  const isEn = String(language).startsWith("en");
  const num = isEn ? abs : abs.replace(".", ",");
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return isEn ? `${sign}${num}%` : `${sign}${num} %`;
};

const compositeId = (row) => {
  const raw = row?.id;
  if (raw == null) return null;
  const parts = String(raw).split(",");
  return parts.length >= 3 ? parts[2] : raw;
};

/**
 * Which per-security list request fetchSecurityRateHistory makes for a row, as
 * { kind, id } for the bulk endpoint, or null when the bulk endpoint does not cover it
 * (listed stock prices, or no id).
 */
export const rateHistoryKind = (row) => {
  const relatedModelType = Number(
    row?.related_model_type ?? String(row?.id || "").split(",")[0]
  );
  const securityId = compositeId(row);
  const market = String(row?.stock_market || "").toUpperCase();
  if (!securityId) return null;
  if (relatedModelType === 30) return { kind: "private_investment_fund", id: String(securityId) };
  if (relatedModelType === 40) return { kind: "investment_fund", id: String(securityId) };
  if (relatedModelType === 10 && row?.nasdaq_id && market && market !== "PRIVATE") return null;
  return { kind: "private_stock", id: String(securityId) };
};

const fetchOneByOne = (rows) =>
  Promise.all(
    rows.map((row) =>
      fetchSecurityRateHistory(row)
        .then((res) => [String(row.id), toRateArray(res?.data)])
        .catch(() => [String(row.id), []])
    )
  ).then((entries) => Object.fromEntries(entries));

/**
 * Rate history for many rows: { [row.id]: rows }. One request for the rows the bulk
 * endpoint covers (it returns per security what fetchSecurityRateHistory got), the rest
 * one by one as before. A server without the bulk endpoint gets the old requests.
 */
export const fetchRateHistories = (rows) => {
  const bulkRows = [];
  const otherRows = [];
  const params = {};
  (rows || []).forEach((row) => {
    const key = rateHistoryKind(row);
    if (!key) {
      otherRows.push(row);
      return;
    }
    bulkRows.push([row, key]);
    params[key.kind] = params[key.kind] || [];
    if (!params[key.kind].includes(key.id)) params[key.kind].push(key.id);
  });

  const bulk = bulkRows.length === 0
    ? Promise.resolve({})
    : httpGetRateHistory(params).then(
      (res) => Object.fromEntries(bulkRows.map(([row, key]) => [
        String(row.id),
        toRateArray(res?.data?.[key.kind]?.[key.id]),
      ])),
      () => fetchOneByOne(bulkRows.map(([row]) => row))
    );

  return Promise.all([bulk, fetchOneByOne(otherRows)]).then(([a, b]) => ({ ...b, ...a }));
};

/** Olemassa olevat kurssi-endpointit — ei backend-muutoksia. */
export const fetchSecurityRateHistory = (row) => {
  const relatedModelType = Number(
    row?.related_model_type ?? String(row?.id || "").split(",")[0]
  );
  const securityId = compositeId(row);
  const market = String(row?.stock_market || "").toUpperCase();
  const params = { limit: 40, offset: 0 };

  if (relatedModelType === 30 && securityId) {
    return httpGetPrivateInvestimentFundDatas({
      ...params,
      ordering: "-date",
      private_investment_fund: securityId,
    });
  }

  if (relatedModelType === 40 && securityId) {
    return httpGetInvestmentFundData({
      ...params,
      ordering: "-date",
      investment_fund: securityId,
    });
  }

  if (relatedModelType === 10 && row?.nasdaq_id && market && market !== "PRIVATE") {
    return httpGetStockData({
      nasdaq_id: row.nasdaq_id,
      stock_market: row.stock_market,
      ordering: "-stock_date",
      limit: 40,
    });
  }

  if (securityId) {
    return httpGetPrivateStockDatas({
      ...params,
      ordering: "-stock_date",
      private_stock: securityId,
    });
  }

  return Promise.resolve({ data: { results: [] } });
};
